import React, { useMemo, useState, useRef, useEffect } from 'react';
import pirateDictionary from './pirateDictionary';
import { playSelectSound } from '../utils/sound';

interface PirateDictionaryPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectTerm: (term: string) => void;
}

const PirateDictionaryPopup: React.FC<PirateDictionaryPopupProps> = ({
    isOpen,
    onClose,
    onSelectTerm
}) => {
    const handleSelectTerm = (term: string) => {
        playSelectSound();
        onSelectTerm(term);
    };

    // Order of categories to display
    const kindOrder = [
        "Greeting",
        "Exclamation",
        "Command",
        "Affirmation",
        "Negation",
        "Insult",
        "Description",
        "Term",
        "Phrase",
        "Verb",
        "Adverb",
        "Object"
    ];

    // Group dictionary entries by kind
    const entriesByKind = useMemo(() => {
        const groupedEntries: Record<string, typeof pirateDictionary> = {};

        pirateDictionary.forEach(entry => {
            if (!groupedEntries[entry.kind]) {
                groupedEntries[entry.kind] = [];
            }
            groupedEntries[entry.kind].push(entry);
        });

        return groupedEntries;
    }, []);

    // Get sorted kinds based on the kindOrder array
    const sortedKinds = useMemo(() => {
        return Object.keys(entriesByKind).sort((a, b) => {
            const indexA = kindOrder.indexOf(a);
            const indexB = kindOrder.indexOf(b);

            if (indexA === -1 && indexB === -1) return a.localeCompare(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    }, [entriesByKind, kindOrder]);

    // Active tab state
    const [activeTab, setActiveTab] = useState(sortedKinds[0] || "");
    const tabsRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    // Dragging event handlers
    const handleMouseDown = (e: React.MouseEvent) => {
        if (!tabsRef.current) return;
        setIsDragging(true);
        setStartX(e.pageX - tabsRef.current.offsetLeft);
        setScrollLeft(tabsRef.current.scrollLeft);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        if (!tabsRef.current) return;
        setIsDragging(true);
        setStartX(e.touches[0].pageX - tabsRef.current.offsetLeft);
        setScrollLeft(tabsRef.current.scrollLeft);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging || !tabsRef.current) return;
        e.preventDefault();
        const x = e.pageX - tabsRef.current.offsetLeft;
        const walk = (x - startX) * 2; // Speed multiplier
        tabsRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging || !tabsRef.current) return;
        const x = e.touches[0].pageX - tabsRef.current.offsetLeft;
        const walk = (x - startX) * 2; // Speed multiplier
        tabsRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleDragEnd = () => {
        setIsDragging(false);
    };

    // Add window event listeners for drag end
    useEffect(() => {
        const endDragging = () => setIsDragging(false);
        window.addEventListener('mouseup', endDragging);
        window.addEventListener('touchend', endDragging);

        return () => {
            window.removeEventListener('mouseup', endDragging);
            window.removeEventListener('touchend', endDragging);
        };
    }, []);

    // Prevent body scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="pirate-dictionary-overlay" onClick={(e) => {
            // Close when clicking the overlay background
            if (e.target === e.currentTarget) {
                onClose();
            }
        }}>
            <div className="pirate-dictionary-popup">
                <div className="popup-header">
                    <h3 className="nes-text is-warning">Pirate Dictionary</h3>
                    <button className="nes-btn is-error close-button" onClick={onClose}>×</button>
                </div>
                <div className="tabs-container">
                    <button className="tab-scroll-btn tab-scroll-left" onClick={() => {
                        if (tabsRef.current) {
                            tabsRef.current.scrollLeft -= 100;
                        }
                    }}>
                        ◀
                    </button>

                    <div
                        className="dictionary-tabs"
                        ref={tabsRef}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleDragEnd}
                        onMouseLeave={handleDragEnd}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleDragEnd}
                    >
                        {sortedKinds.map(kind => (
                            <button
                                key={kind}
                                className={`tab-button ${activeTab === kind ? 'active-tab' : ''}`}
                                onClick={() => setActiveTab(kind)}
                            >
                                {kind}
                            </button>
                        ))}
                    </div>

                    <button className="tab-scroll-btn tab-scroll-right" onClick={() => {
                        if (tabsRef.current) {
                            tabsRef.current.scrollLeft += 100;
                        }
                    }}>
                        ▶
                    </button>
                </div>
                <div className="dictionary-content">
                    {entriesByKind[activeTab]?.map((entry, index) => (
                        <div
                            key={`${activeTab}-${index}`}
                            className="dictionary-entry"
                            onClick={() => handleSelectTerm(entry.slang)}
                        >
                            <span className="slang nes-text is-primary">{entry.slang}</span>
                            <span className="meaning">{entry.meaning}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PirateDictionaryPopup;
